import {
  findSourceLocationMapping,
  isLocationFieldMappingCode,
} from 'src/utils/fieldMappingSaveUtils'

type MappingRow = Record<string, any>

/** Groups using unified this-page dependent pair formation (expand, then remove legacy). */
export const NEW_DEPENDENT_PAIR_FORMATION_GROUP_CODES = ['ORDER_PAYOUT', 'ORDER'] as const

export type NewDependentPairFormationGroupCode =
  (typeof NEW_DEPENDENT_PAIR_FORMATION_GROUP_CODES)[number]

export const usesNewDependentPairFormation = (groupCode?: string | null): boolean =>
  NEW_DEPENDENT_PAIR_FORMATION_GROUP_CODES.includes(
    `${groupCode ?? ''}` as NewDependentPairFormationGroupCode
  )

export type ResolveDependentMappingPartnerArgs = {
  anchor: MappingRow | null | undefined
  oppositeMappings?: MappingRow[] | null
  groupCode?: string | null
  /** ORDER → SHIPMENT fallback (3d). */
  shippingOppositeMappings?: MappingRow[] | null
  /** For LOCATION helper (3c) when opposite list is grouped. */
  oppositeSettingsGroups?: Array<{group_code?: string; mappings_data?: MappingRow[]}>
  /** Disambiguate when multiple opposite rows link to the same anchor id. */
  preferredSettingMappingId?: number | null
}

const isNonDefaultPairRow = (row: MappingRow | null | undefined): boolean =>
  Boolean(row) && row?.isDefault === false

const normalizeAnchorId = (anchor: MappingRow): number | null => {
  const id = anchor?.id
  if (id === null || id === undefined || id === '') return null
  const numeric = Number(id)
  return Number.isNaN(numeric) ? null : numeric
}

const findOppositeRowsLinkingToAnchor = (
  oppositeMappings: MappingRow[],
  anchorId: number
): MappingRow[] =>
  oppositeMappings.filter((row) => {
    if (!isNonDefaultPairRow(row)) return false
    const mapWithId = row?.map_with?.id
    if (mapWithId === null || mapWithId === undefined || mapWithId === '') return false
    return Number(mapWithId) === anchorId
  })

const pickOppositeRowCandidate = (
  candidates: MappingRow[],
  preferredSettingMappingId?: number | null
): MappingRow | null => {
  if (!candidates.length) return null
  if (candidates.length === 1) return candidates[0]

  if (preferredSettingMappingId !== null && preferredSettingMappingId !== undefined) {
    const preferred = candidates.find(
      (row) => Number(row?.setting_mapping_id) === Number(preferredSettingMappingId)
    )
    if (preferred) return preferred
  }

  return candidates[0]
}

const findOppositeBySameApiCode = (
  oppositeMappings: MappingRow[],
  anchor: MappingRow
): MappingRow | null => {
  const apiCode = anchor?.api_code ?? anchor?.field_code ?? null
  if (!apiCode) return null

  return (
    oppositeMappings.find(
      (row) =>
        isNonDefaultPairRow(row) &&
        (row?.api_code === apiCode || row?.field_code === apiCode)
    ) ?? null
  )
}

const findShippingFallbackPartner = (
  anchor: MappingRow,
  shippingOppositeMappings: MappingRow[] | null | undefined
): MappingRow | null => {
  if (!shippingOppositeMappings?.length) return null
  return findOppositeBySameApiCode(shippingOppositeMappings, anchor)
}

/**
 * Resolve the partner field for a dependent pair anchored on this-page mappings_data.
 * Steps: map_with → opposite map_with.id === anchor.id → opposite same api_code (non-default)
 * → LOCATION (3c) → ORDER SHIPMENT list (3d) → null.
 */
export const resolveDependentMappingPartner = ({
  anchor,
  oppositeMappings = [],
  groupCode,
  shippingOppositeMappings,
  oppositeSettingsGroups = [],
  preferredSettingMappingId,
}: ResolveDependentMappingPartnerArgs): MappingRow | null => {
  if (!anchor || !isNonDefaultPairRow(anchor)) {
    return null
  }

  if (anchor.map_with) {
    return anchor.map_with as MappingRow
  }

  const opposite = oppositeMappings ?? []
  const anchorId = normalizeAnchorId(anchor)

  if (anchorId !== null) {
    const backlinkCandidates = findOppositeRowsLinkingToAnchor(opposite, anchorId)
    const backlinkPartner = pickOppositeRowCandidate(
      backlinkCandidates,
      preferredSettingMappingId
    )
    if (backlinkPartner) {
      return backlinkPartner
    }
  }

  const apiCodePartner = findOppositeBySameApiCode(opposite, anchor)
  if (apiCodePartner) {
    return apiCodePartner
  }

  if (isLocationFieldMappingCode(anchor)) {
    const locationPartner =
      findSourceLocationMapping(oppositeSettingsGroups, groupCode ?? undefined, anchor) ??
      findSourceLocationMapping(
        [{group_code: groupCode ?? undefined, mappings_data: opposite}],
        groupCode ?? undefined,
        anchor
      )
    if (locationPartner) {
      return locationPartner
    }
  }

  if (`${groupCode ?? ''}` === 'ORDER') {
    const shipmentPartner = findShippingFallbackPartner(anchor, shippingOppositeMappings)
    if (shipmentPartner) {
      return shipmentPartner
    }
  }

  return null
}
